import { useState } from 'react';
import { signup } from '../services/api';
import { useT } from '../i18n';
import type { NavView } from '../components/Layout';

interface SignupProps {
  onNavigate?: (view: NavView) => void;
}

export default function Signup({ onNavigate }: SignupProps) {
  const t = useT();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!consent) {
      setError(t('signup.consentRequired'));
      return;
    }
    setLoading(true);
    try {
      await signup(name, email, password, companyName, consent);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signup.error'));
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <section className="mx-auto max-w-md rounded-lg border border-brand-200 bg-white p-8 shadow-sm text-center">
        <h1 className="text-2xl font-bold text-brand-900">{t('signup.successTitle')}</h1>
        <p className="mt-4 text-sm text-brand-600">{t('signup.successBody')}</p>
        <p className="mt-2 text-sm text-gray-500">{t('common.checkSpam')}</p>
        <button
          type="button"
          onClick={() => onNavigate?.('login')}
          className="mt-6 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
        >
          {t('signup.goLogin')}
        </button>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-md rounded-lg border border-brand-200 bg-white p-8 shadow-sm">
      <h1 className="mb-1 text-2xl font-bold text-brand-900">{t('signup.title')}</h1>
      <p className="mb-6 text-sm text-brand-600">{t('signup.subtitle')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium text-brand-700">
            {t('signup.name')}
          </label>
          <input
            id="name"
            type="text"
            required
            minLength={2}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="company" className="mb-1 block text-sm font-medium text-brand-700">
            {t('signup.company')}
          </label>
          <input
            id="company"
            type="text"
            required
            minLength={2}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-brand-700">
            {t('signup.email')}
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-brand-700">
            {t('signup.password')}
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-brand-500">{t('signup.passwordHint')}</p>
        </div>

        <div className="flex items-start gap-2">
          <input
            id="consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-brand-300 text-brand-600 focus:ring-brand-500"
          />
          {/* Issue #253: o aceite deve referenciar os documentos legais reais
              (Termos de Uso + Politica de Privacidade) com links — LGPD Art. 8. */}
          <label htmlFor="consent" className="text-sm text-brand-600">
            {t('signup.consentPrefix')}{' '}
            <button
              type="button"
              // preventDefault evita que o <label> marque o checkbox ao clicar no link
              onClick={(e) => { e.preventDefault(); onNavigate?.('terms'); }}
              className="text-brand-700 font-medium underline"
            >
              {t('common.legal.terms')}
            </button>{' '}
            {t('signup.consentAnd')}{' '}
            <button
              type="button"
              // preventDefault evita que o <label> marque o checkbox ao clicar no link
              onClick={(e) => { e.preventDefault(); onNavigate?.('privacy'); }}
              className="text-brand-700 font-medium underline"
            >
              {t('common.legal.privacy')}
            </button>
          </label>
        </div>

        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {loading ? t('signup.submitting') : t('signup.submit')}
        </button>
      </form>

      <p className="text-center text-sm text-brand-600 mt-4">
        {t('signup.haveAccount')}{' '}
        <button
          type="button"
          onClick={() => onNavigate?.('login')}
          className="text-brand-700 font-medium underline"
        >
          {t('signup.loginLink')}
        </button>
      </p>
    </section>
  );
}
